import { profileBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  getProfileActiveTravelPasses,
  getProfileHistoryTravelPasses,
} from '@/features/profile/profileActions';
import {
  activeTravelPassesProfileSelector,
  historyTravelPassesProfileSelector,
} from '@/features/profile/profileSelectors';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import BookingItem from '@/routes/account/layout/BookingItem';
import BookingNoItems from '@/routes/account/layout/BookingNoItems';
import { useDispatch, useSelector } from '@/store/utils';
import type { ProfileBookingTimeType } from '@/types/profile';
import { useCallback, useMemo, type FC } from 'react';

interface TravelPassesListProps {
  timeType: ProfileBookingTimeType;
}

const TravelPassesList: FC<TravelPassesListProps> = ({ timeType }) => {
  const dispatch = useDispatch();

  const isActive = timeType === 'active';

  const loading = useSelector(profileBookingSearchLoadingSelector);
  const travelPasses = useSelector(
    isActive
      ? activeTravelPassesProfileSelector
      : historyTravelPassesProfileSelector
  );

  const hasMore = useMemo(() => {
    if (!travelPasses) return false;
    return travelPasses.data.length < travelPasses.totalCount;
  }, [travelPasses]);

  const handleLoadMore = useCallback(() => {
    dispatch(
      isActive
        ? getProfileActiveTravelPasses()
        : getProfileHistoryTravelPasses()
    );
  }, [dispatch, isActive]);

  const loadMoreRef = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore: handleLoadMore,
  });

  if (!travelPasses?.data.length) {
    return <BookingNoItems bookingType="travelPasses" timeType={timeType} />;
  }

  return (
    <div className="flex w-full flex-col laptop:mt-2 laptop:max-w-[690px]">
      <div className="flex w-full flex-col gap-4">
        {travelPasses.data.map((travelPass) => (
          <BookingItem
            key={travelPass.id}
            booking={travelPass}
            isActive={isActive}
          />
        ))}
      </div>
      {hasMore && <div ref={loadMoreRef} />}
    </div>
  );
};

export default TravelPassesList;
